/* eslint-disable react/prop-types */
/**
 * Map of ONE deal's lease/deed tracts — the deal detail page's Map tab.
 *
 * Draws this deal's aliquot tract shapes over the PLSS sections of its
 * county/counties, with an optional "Show neighbouring deals" overlay: the
 * other deals with tracts in the same sections (the competing offers on the
 * same quarter calls). Data is the same server-scoped set the old deals map
 * used (all tracts / lease_deeds / deals the viewer may see), so a neighbour
 * is only ever a deal the viewer is allowed to see.
 *
 * Mounted only while its tab is open (the deal page's Tabs use
 * destroyInactiveTabPane), so nothing here loads until the tab is opened.
 *
 * Without a `dealId` it maps a SET of deals instead — the deals list's map
 * band. `dealIds` is the set the grid is showing (null = every deal the
 * viewer can see); there is no "this deal" and no neighbour overlay, and
 * below DETAIL_ZOOM each section with tracts is one dot (utils/sectionDots).
 */
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Descriptions,
  Divider,
  Drawer,
  Empty,
  Space,
  Spin,
  Typography,
} from "antd";
import { useNavigate } from "react-router";
import { WigMap } from "@warwick-investment-group/wig-react-library";

import { AppContext } from "../../AppContextProvider";
import useMapboxToken from "../../hooks/useMapboxToken";
import useSpatialData from "../../hooks/useSpatialData";
import useOccUnits from "../../hooks/useOccUnits";
import useOccIntents from "../../hooks/useOccIntents";
import useDealTracts from "../../hooks/useDealTracts";
import useLookupLabels from "../../hooks/useLookupLabels";
import useMapSymbolLayer from "../../hooks/useMapSymbolLayer";
import { aggregateBySection, normalizeCounty } from "../../utils/trsAggregation";
import {
  OCC_INTENT_COLOR,
  OCC_KIND_COLORS,
  TRACT_BUCKET_COLORS,
  TRACT_LINE_COLOR,
} from "../../utils/mapRamps";
import {
  OCC_INTENTS_LABEL,
  OCC_INTENTS_OPTION,
  OCC_KINDS,
  occKindLabel,
} from "../../utils/occUnits";
import { OCC_KIND_LETTERS, groupOccFootprints, occFilingRows } from "../../utils/occFootprints";
import { groupOccIntents, occIntentRows } from "../../utils/occIntents";
import { canonicalTrs, indexSectionsByTrs } from "../../utils/aliquot";
import { buildTractShapes } from "../../utils/tractShapes";
import { TRACT_BUCKETS, statusBucket } from "../../utils/tractStatusBucket";
import { sectionDotFeatures } from "../../utils/sectionDots";
import { tractGroupSummary, tractSummaryRows } from "../../utils/tractSummary";
import { joinTractRows } from "../../utils/dealTracts";
import {
  bboxOfFeatures,
  dealSetTracts,
  splitDealTracts,
  tractCounties,
} from "../../utils/mapScope";
import { esc, renderPopupHtml } from "../../utils/mapPopup";
import {
  countyLabelPoints,
  polygonLabelPoint,
  sectionLabelPoints,
  townshipLabelPoints,
} from "../../utils/mapLabels";
import { isOpen } from "../../utils/dealMetrics";
import { CLOSED_STATUS, DEAD_STATUSES } from "../../utils/dealStatusVocab";
import DealsMapLegend from "../dealsMapLegend";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
// Until the deal's tracts are known (WigMap reads its initial view once).
const FALLBACK_VIEW = { center: [-97.5, 35.5], zoom: 6 };
const MAP_STYLE = "mapbox://styles/mapbox/light-v11";
const MAP_HEIGHT = "70vh";
// Camera fit on the deal's tracts.
const FIT_MAX_ZOOM = 14;
const FIT_PADDING = 40;
// Section outlines appear from here. For one deal, tract shapes draw at
// every zoom (its tracts are few); for a set of deals they draw from here
// and each section is a single dot below it.
const SECTION_LINE_MIN_ZOOM = 9.5;
const DETAIL_ZOOM = SECTION_LINE_MIN_ZOOM;
// Clicking a section dot flies here, straight into the tract shapes.
const DOT_CLICK_ZOOM = 12;
const SECTION_DOTS_ID = "section-dots";
// Zoom bands for the label layers.
const COUNTY_LABEL_MAX_ZOOM = 10;
const TOWNSHIP_LABEL_MIN_ZOOM = 9;
const SECTION_LABEL_ZOOM = 11;

const ACTIVE_DEFINITION = `Active = every status except ${CLOSED_STATUS} and dead (${[
  ...DEAD_STATUSES,
].join(", ")})`;

const COUNTY_SCOPE_LINE_COLOR = "#3a3a3a";
// This deal's tracts: the strong highlight outline. A clicked neighbour's
// tracts: a second, distinct outline.
const OWN_OUTLINE_COLOR = "#00b8d4";
const SELECTED_OUTLINE_COLOR = "#ff6d00";

const OCC_FILL_ID = "occ-fill";
const OCC_LINE_ID = "occ-line";
const OCC_BADGES_ID = "occ-badges";
const OCC_LINE_EXPRESSION = [
  "match",
  ["get", "primary_kind"],
  ...Object.entries(OCC_KIND_COLORS).flat(),
  "#555",
];
// OCC intents to drill (well permits): circles above every polygon layer.
const OCC_INTENTS_ID = "occ-intents";
// Control-group / legend heading. Source literal (no metadata home for it).
const OCC_GROUP_LABEL = "OCC filings, last 12 months";

const TRACT_FILL_EXPRESSION = [
  "match",
  ["get", "bucket"],
  ...Object.entries(TRACT_BUCKET_COLORS).flat(),
  TRACT_BUCKET_COLORS.early,
];
const EMPTY_FC = { type: "FeatureCollection", features: [] };

// Two tract sets, each its own layer stack: neighbours subdued and below,
// this deal strong and on top.
const NBR = "tracts-nbr";
const OWN = "tracts-own";
const SELECTED_ID = "tracts-selected";
const TRACT_BADGES_ID = "tracts-badges";
const setIds = (prefix) => ({
  fill: `${prefix}-fill`,
  line: `${prefix}-line`,
  approx: `${prefix}-line-approx`,
  unresolvedFill: `${prefix}-unresolved-fill`,
  unresolvedLine: `${prefix}-unresolved-line`,
});
const SET_STYLE = {
  [NBR]: { fillOpacity: 0.25, lineColor: TRACT_LINE_COLOR, lineWidth: 0.6 },
  [OWN]: { fillOpacity: 0.65, lineColor: OWN_OUTLINE_COLOR, lineWidth: 3 },
};
const LABEL_LAYER_IDS = [
  "county-labels",
  "township-labels",
  "section-labels",
  TRACT_BADGES_ID,
  OCC_BADGES_ID,
];
// Fonts the light-v11 style ships glyphs for; an unknown stack drops labels silently.
const LABEL_TEXT_FONT = ["DIN Pro Medium", "Arial Unicode MS Regular"];
const LABEL_HALO = { "text-color": "#333", "text-halo-color": "#fff", "text-halo-width": 1.2 };

// GeoJSON layers for one tract set (see SET_STYLE).
const tractSetLayers = (prefix, shapes) => {
  const ids = setIds(prefix);
  const st = SET_STYLE[prefix];
  return [
    {
      type: "geojson",
      id: ids.fill,
      data: shapes.fillFC,
      layerType: "fill",
      paint: { "fill-color": TRACT_FILL_EXPRESSION, "fill-opacity": st.fillOpacity },
    },
    {
      type: "geojson",
      id: ids.line,
      data: shapes.lineFC,
      layerType: "line",
      paint: { "line-color": st.lineColor, "line-width": st.lineWidth },
    },
    // Approximate (lotted / irregular section): dotted edge.
    {
      type: "geojson",
      id: ids.approx,
      data: shapes.approxLineFC,
      layerType: "line",
      paint: {
        "line-color": st.lineColor,
        "line-width": Math.max(1, st.lineWidth - 1),
        "line-dasharray": [0.5, 2],
      },
    },
    // Unresolved quartering: dashed section outline, no visible fill (the
    // near-invisible fill is only there for hover).
    {
      type: "geojson",
      id: ids.unresolvedFill,
      data: shapes.unresolvedFC,
      layerType: "fill",
      paint: { "fill-color": "#000", "fill-opacity": 0.01 },
    },
    {
      type: "geojson",
      id: ids.unresolvedLine,
      data: shapes.unresolvedFC,
      layerType: "line",
      paint: {
        "line-color": st.lineColor,
        "line-width": Math.max(1, st.lineWidth - 1),
        "line-dasharray": [3, 2],
      },
    },
  ];
};

// Summary rows for a shape holding several deals — the tract hover and the
// top of the click side panel. Wording is a source literal (no metadata home
// for derived counts).
const groupSummaryRows = (groupRows) => {
  const s = tractGroupSummary(groupRows, {
    bucketOf: statusBucket,
    buckets: TRACT_BUCKETS,
    isOpen,
  });
  return [
    { label: "Deals", text: `${s.dealCount} (${s.activeCount} active)` },
    { label: "Status", text: s.statusMix },
    { label: "Owners", text: String(s.ownerCount) },
    { label: "Net acres", text: s.netAcres.toLocaleString(undefined, { maximumFractionDigits: 2 }) },
    { label: "Brokers", text: s.brokers },
  ];
};

// One row per distinct deal among a shape's tract rows (first tract wins).
const distinctDealRows = (rows) => {
  const seen = new Set();
  const out = [];
  for (const r of Array.isArray(rows) ? rows : []) {
    if (seen.has(r.deal_id)) continue;
    seen.add(r.deal_id);
    out.push(r);
  }
  return out;
};

const numOrZero = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const shapeCount = (shapes) =>
  shapes.fillFC.features.length + shapes.unresolvedFC.features.length;

/**
 * `data` (optional) is the deals/lease-deeds/tracts, field metadata and broker
 * labels the host page already loaded, shaped like useDealTracts' return plus
 * `brokerLabels`. When given, the map fetches none of it itself (the deals
 * list's map band); when omitted, it loads its own (Map tab, deal detail).
 */
const DealMap = ({ dealId, dealIds = null, height = MAP_HEIGHT, darkMode, data = null }) => {
  const setMode = dealId === null || dealId === undefined;
  const { theme } = useContext(AppContext);
  const navigate = useNavigate();
  const legendTheme = darkMode === undefined ? theme : darkMode ? "dark" : "light";

  const {
    token: kvMapboxToken,
    loading: mapboxTokenLoading,
    errorMsg: mapboxTokenErr,
  } = useMapboxToken();
  // Local-dev fallback: VITE_MAPBOX_TOKEN when Key Vault fails.
  const envMapboxToken = import.meta.env.VITE_MAPBOX_TOKEN || "";
  const usingEnvFallback = !!mapboxTokenErr && !kvMapboxToken && !!envMapboxToken;
  const mapboxToken = kvMapboxToken || (usingEnvFallback ? envMapboxToken : "");

  const ownData = useDealTracts({ enabled: !data });
  const { labelByLookup } = useLookupLabels({ enabled: !data });
  const {
    tracts,
    leaseDeeds,
    deals,
    tractMetadata,
    dealMetadata,
    leaseDeedMetadata,
    loading: dealsLoading,
    errorMsg: dealsErr,
  } = data || ownData;
  const brokerLabels = data ? data.brokerLabels : labelByLookup?.brokers_xref;

  const allRows = useMemo(
    () => joinTractRows(tracts, leaseDeeds, deals, { brokerLabels }),
    [tracts, leaseDeeds, deals, brokerLabels]
  );
  const mapRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const [showNeighbours, setShowNeighbours] = useState(false);
  // One deal: applies to neighbours only, the deal's own tracts always draw.
  // A set of deals: applies to the set.
  const [showDead, setShowDead] = useState(false);
  // Set mode swaps section dots for tract shapes at DETAIL_ZOOM.
  const [zoom, setZoom] = useState(FALLBACK_VIEW.zoom);
  const detailZoom = !setMode || zoom >= DETAIL_ZOOM;

  const { own, neighbours } = useMemo(
    () =>
      setMode
        ? { own: dealSetTracts(allRows, dealIds, { includeDead: showDead }), neighbours: [] }
        : splitDealTracts(allRows, dealId),
    [allRows, dealId, dealIds, setMode, showDead]
  );
  const [occKinds, setOccKinds] = useState(["pooling"]);
  // Intents to drill share the OCC control but are not a unit kind.
  const [showIntents, setShowIntents] = useState(false);
  // Tract clicked: its shape's deal rows, in the side panel; all tracts of
  // those deals are outlined until it closes.
  const [selectedTract, setSelectedTract] = useState(null);

  const shownNeighbours = useMemo(
    () =>
      showNeighbours
        ? neighbours.filter((r) => showDead || statusBucket(r.deal_status) !== "dead")
        : [],
    [neighbours, showNeighbours, showDead]
  );
  const drawnRows = useMemo(() => [...own, ...shownNeighbours], [own, shownNeighbours]);

  // Sections + OCC: only the county/counties of THIS deal's tracts.
  const ownCounties = useMemo(() => tractCounties(own), [own]);
  const { states, counties, sections, loading: spatialLoading, errorMsg: spatialErr } =
    useSpatialData({ counties: ownCounties });
  // Only the kinds switched on: footprints are built from those alone (see
  // groupOccFootprints), and the hook fetches a kind the first time it is
  // switched on. Asking for all four loaded three kinds nobody had picked
  // into every slow county request.
  const {
    unitsByKind,
    metadata: occMetadata,
    loading: occLoading,
    errorMsg: occErr,
  } = useOccUnits({ counties: ownCounties, kinds: occKinds });
  const {
    intents: occIntentsFC,
    metadata: occIntentMetadata,
    loading: occIntentsLoading,
    errorMsg: occIntentsErr,
  } = useOccIntents({ counties: ownCounties, enabled: showIntents });

  const sectionIndex = useMemo(() => indexSectionsByTrs(sections), [sections]);
  // Drawn per set (dead filtering already applied above)…
  const ownShapes = useMemo(
    () => buildTractShapes(own, sectionIndex, { includeDead: true }),
    [own, sectionIndex]
  );
  const nbrShapes = useMemo(
    () => buildTractShapes(shownNeighbours, sectionIndex, { includeDead: true }),
    [shownNeighbours, sectionIndex]
  );
  // …but grouped together: group ids are section + aliquot, so a hover on
  // either set finds every deal on that aliquot, and badges count them all.
  const allShapes = useMemo(
    () => buildTractShapes(drawnRows, sectionIndex, { includeDead: true }),
    [drawnRows, sectionIndex]
  );
  const hasOwnShapes = shapeCount(ownShapes) > 0;
  const showOwn = detailZoom && hasOwnShapes;
  const showNbr = showNeighbours && shapeCount(nbrShapes) > 0;
  // Set mode, zoomed out: one dot per section instead of the tract shapes.
  const sectionDotsFC = useMemo(
    () => (setMode ? sectionDotFeatures(own, sectionIndex, { includeDead: true }) : EMPTY_FC),
    [setMode, own, sectionIndex]
  );
  const dotsVisible = !detailZoom && sectionDotsFC.features.length > 0;

  // Section hover: the drawn deals in that section.
  const aggregated = useMemo(() => aggregateBySection(drawnRows), [drawnRows]);
  const sectionsFC = useMemo(() => {
    if (!sections || !Array.isArray(sections.features)) return EMPTY_FC;
    return {
      type: "FeatureCollection",
      features: sections.features.map((f) => {
        const props = f?.properties || {};
        const key = canonicalTrs(props.twp, props.rng, props.sec);
        const b = key ? aggregated.get(key) : null;
        return {
          ...f,
          properties: {
            ...props,
            count: b ? b.openCount : 0,
            dealCount: b ? b.count : 0,
            netAcres: b ? b.netAcres : 0,
            topBroker: b?.topBroker ?? null,
          },
        };
      }),
    };
  }, [sections, aggregated]);
  const sectionsHitFC = useMemo(
    () => ({
      type: "FeatureCollection",
      features: sectionsFC.features.filter((f) => numOrZero(f.properties.dealCount) > 0),
    }),
    [sectionsFC]
  );

  // OCC filings grouped by identical footprint, switched-on kinds only,
  // last 12 months (see utils/occFootprints).
  const occFootprints = useMemo(
    () =>
      groupOccFootprints(OCC_KINDS.flatMap((k) => unitsByKind[k]?.features || []), {
        kinds: occKinds,
      }),
    [occKinds, unitsByKind]
  );
  const showOcc = occFootprints.fc.features.length > 0;
  const drawnOccKinds = useMemo(() => {
    const present = new Set();
    for (const filings of occFootprints.filingsById.values()) {
      for (const f of filings) present.add(f.kind);
    }
    return OCC_KINDS.filter((k) => present.has(k));
  }, [occFootprints]);
  // Intents to drill, one point per spot, last 12 months (utils/occIntents).
  const occIntents = useMemo(
    () => groupOccIntents(showIntents ? occIntentsFC.features : []),
    [showIntents, occIntentsFC]
  );
  const showIntentPoints = occIntents.fc.features.length > 0;

  // Outline every drawn tract of a clicked NEIGHBOUR deal; this deal is
  // already outlined.
  const selectedFC = useMemo(() => {
    const features = [];
    for (const r of selectedTract?.deals || []) {
      if (String(r.deal_id) === String(dealId)) continue;
      features.push(...(allShapes.shapesByDeal.get(String(r.deal_id)) || []));
    }
    return { type: "FeatureCollection", features };
  }, [selectedTract, allShapes, dealId]);
  const showSelected = selectedFC.features.length > 0;

  const tractSummaryMeta = useMemo(
    () => ({ deals: dealMetadata, lease_deeds: leaseDeedMetadata, lease_deed_tracts: tractMetadata }),
    [dealMetadata, leaseDeedMetadata, tractMetadata]
  );

  const ownCountyNames = useMemo(() => ownCounties.map((c) => c.county), [ownCounties]);
  const ownCountiesFC = useMemo(() => {
    const want = new Set(ownCounties.map((c) => `${c.state}|${c.county}`));
    return {
      type: "FeatureCollection",
      features: (counties?.features || []).filter((f) => {
        const p = f?.properties || {};
        return want.has(`${String(p.state ?? "").trim().toUpperCase()}|${normalizeCounty(p.county)}`);
      }),
    };
  }, [counties, ownCounties]);

  // Camera: fit this deal's tract shapes once they exist (once per deal) —
  // in set mode, once per set, so a new search re-frames the map.
  // WigMap reads its initial view only at creation, so this goes through the
  // map instance.
  const fitKey = useMemo(
    () =>
      setMode
        ? `set:${dealIds ? [...dealIds].map(String).sort().join(",") : "all"}`
        : `deal:${dealId}`,
    [setMode, dealIds, dealId]
  );
  const fittedRef = useRef(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || fittedRef.current === fitKey) return;
    const bb = bboxOfFeatures([...ownShapes.fillFC.features, ...ownShapes.unresolvedFC.features]);
    if (!bb) return;
    fittedRef.current = fitKey;
    try {
      map.fitBounds(
        [
          [bb[0], bb[1]],
          [bb[2], bb[3]],
        ],
        { padding: FIT_PADDING, maxZoom: FIT_MAX_ZOOM, duration: 0 }
      );
    } catch {
      // map torn down mid-change
    }
  }, [fitKey, ownShapes, mapReady]);

  // Set mode: track zoom so dots / tract shapes swap at DETAIL_ZOOM. zoomend
  // fires once when zoom motion stops; only one threshold matters.
  useEffect(() => {
    const map = mapRef.current;
    if (!setMode || !map) return undefined;
    const onZoom = () => {
      try {
        setZoom(map.getZoom());
      } catch {
        // map may have been removed already
      }
    };
    onZoom();
    map.on("zoomend", onZoom);
    return () => {
      try {
        map.off("zoomend", onZoom);
      } catch {
        // map already torn down
      }
    };
  }, [setMode, mapReady]);

  // GeoJSON layers, bottom -> top: sections, county / state lines, OCC,
  // neighbours, this deal, selected neighbour outline, OCC intents to drill. Labels and badges are
  // map-instance layers stacked on top.
  const layers = useMemo(() => {
    const ls = [];
    if (sectionsHitFC.features.length > 0) {
      ls.push({
        type: "geojson",
        id: "sections-hit",
        data: sectionsHitFC,
        layerType: "fill",
        paint: { "fill-color": "#000", "fill-opacity": 0.01 },
      });
    }
    if (sectionsFC.features.length > 0) {
      ls.push({
        type: "geojson",
        id: "sections-line",
        data: sectionsFC,
        layerType: "line",
        paint: {
          "line-color": "#8c8c8c",
          "line-width": 0.6,
          "line-opacity": ["step", ["zoom"], 0, SECTION_LINE_MIN_ZOOM, 0.7],
        },
      });
    }
    if (counties?.features?.length > 0) {
      ls.push({
        type: "geojson",
        id: "counties-line",
        data: counties,
        layerType: "line",
        paint: { "line-color": "#8c8c8c", "line-width": 0.6, "line-opacity": 0.8 },
      });
    }
    if (ownCountiesFC.features.length > 0) {
      ls.push({
        type: "geojson",
        id: "counties-scope-line",
        data: ownCountiesFC,
        layerType: "line",
        paint: { "line-color": COUNTY_SCOPE_LINE_COLOR, "line-width": 1.4 },
      });
    }
    if (states?.features?.length > 0) {
      ls.push({
        type: "geojson",
        id: "states-line",
        data: states,
        layerType: "line",
        paint: {
          "line-color": "#333333",
          "line-width": ["interpolate", ["linear"], ["zoom"], 3, 1.5, 8, 0.6],
          "line-opacity": ["interpolate", ["linear"], ["zoom"], 3, 0.9, 9, 0.2],
        },
      });
    }
    if (showOcc) {
      ls.push({
        type: "geojson",
        id: OCC_FILL_ID,
        data: occFootprints.fc,
        layerType: "fill",
        paint: { "fill-color": "#000", "fill-opacity": 0.01 },
      });
      ls.push({
        type: "geojson",
        id: OCC_LINE_ID,
        data: occFootprints.fc,
        layerType: "line",
        paint: { "line-color": OCC_LINE_EXPRESSION, "line-width": 1.5, "line-opacity": 0.9 },
      });
    }
    if (showNbr) ls.push(...tractSetLayers(NBR, nbrShapes));
    if (showOwn) ls.push(...tractSetLayers(OWN, ownShapes));
    if (showSelected) {
      ls.push({
        type: "geojson",
        id: SELECTED_ID,
        data: selectedFC,
        layerType: "line",
        paint: { "line-color": SELECTED_OUTLINE_COLOR, "line-width": 3.5 },
      });
    }
    if (showIntentPoints) {
      ls.push({
        type: "geojson",
        id: OCC_INTENTS_ID,
        data: occIntents.fc,
        layerType: "circle",
        paint: {
          "circle-color": OCC_INTENT_COLOR,
          "circle-radius": 5,
          "circle-stroke-color": "#fff",
          "circle-stroke-width": 1.5,
        },
      });
    }
    return ls;
  }, [
    sectionsFC,
    sectionsHitFC,
    counties,
    ownCountiesFC,
    states,
    showOcc,
    occFootprints,
    showNbr,
    nbrShapes,
    showOwn,
    ownShapes,
    showSelected,
    selectedFC,
    showIntentPoints,
    occIntents,
  ]);

  // WigMap appends each layer on top as it is first added; re-stack after
  // every change so neighbours stay under this deal (toggled on later) and
  // labels stay on top. Runs after WigMap's own sync (child effects first).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getLayer) return;
    const order = [
      ...(showOcc ? [OCC_FILL_ID, OCC_LINE_ID] : []),
      ...(showNbr ? Object.values(setIds(NBR)) : []),
      ...(showOwn ? Object.values(setIds(OWN)) : []),
      ...(showSelected ? [SELECTED_ID] : []),
      ...(showIntentPoints ? [OCC_INTENTS_ID] : []),
      ...(setMode ? [SECTION_DOTS_ID] : []),
      ...LABEL_LAYER_IDS,
    ];
    for (const id of order) {
      try {
        if (map.getLayer(id)) map.moveLayer(id);
      } catch {
        // style not ready / layer removed mid-change; the next change re-stacks
      }
    }
  }, [layers, showOcc, showNbr, showOwn, showSelected, showIntentPoints, setMode, mapReady]);

  // Labels (map-instance symbol layers, see useMapSymbolLayer).
  const countyLabelsFC = useMemo(
    () => countyLabelPoints(counties, ownCountyNames),
    [counties, ownCountyNames]
  );
  const townshipLabelsFC = useMemo(() => townshipLabelPoints(sections), [sections]);
  const sectionLabelsFC = useMemo(() => sectionLabelPoints(sections), [sections]);
  useMapSymbolLayer(mapRef, mapReady, {
    id: "county-labels",
    data: countyLabelsFC,
    maxzoom: COUNTY_LABEL_MAX_ZOOM,
    layout: {
      "text-field": ["get", "name"],
      "text-size": ["case", ["get", "selected"], 16, 11],
      "text-font": LABEL_TEXT_FONT,
      "symbol-sort-key": ["case", ["get", "selected"], 0, 1],
    },
    paint: { ...LABEL_HALO, "text-color": ["case", ["get", "selected"], "#000", "#555"] },
  });
  // Set mode, zoomed out: one dot per section with tracts (the tract shapes
  // take over at DETAIL_ZOOM). Circle layer through the map-instance path:
  // WigMap layers have no zoom range.
  useMapSymbolLayer(mapRef, mapReady, {
    id: SECTION_DOTS_ID,
    type: "circle",
    data: sectionDotsFC,
    maxzoom: DETAIL_ZOOM,
    paint: {
      "circle-color": TRACT_FILL_EXPRESSION,
      "circle-radius": ["get", "radius"],
      "circle-stroke-color": TRACT_LINE_COLOR,
      "circle-stroke-width": 1,
      "circle-opacity": 0.9,
    },
  });
  useMapSymbolLayer(mapRef, mapReady, {
    id: "township-labels",
    data: townshipLabelsFC,
    minzoom: TOWNSHIP_LABEL_MIN_ZOOM,
    maxzoom: SECTION_LABEL_ZOOM,
    layout: { "text-field": ["get", "label"], "text-size": 12, "text-font": LABEL_TEXT_FONT },
    paint: LABEL_HALO,
  });
  // Count badge on an aliquot holding more than one drawn deal.
  useMapSymbolLayer(mapRef, mapReady, {
    id: TRACT_BADGES_ID,
    data: showOwn || showNbr ? allShapes.badgeFC : EMPTY_FC,
    layout: {
      "text-field": ["to-string", ["get", "count"]],
      "text-size": 11,
      "text-font": LABEL_TEXT_FONT,
      "text-allow-overlap": true,
    },
    paint: { "text-color": "#fff", "text-halo-color": "#1f1f1f", "text-halo-width": 2 },
  });
  const occBadgesFC = useMemo(() => {
    const features = [];
    for (const f of occFootprints.fc.features) {
      const pt = polygonLabelPoint(f.geometry);
      if (pt) {
        features.push({
          type: "Feature",
          geometry: { type: "Point", coordinates: pt },
          properties: { badge: f.properties.badge },
        });
      }
    }
    return { type: "FeatureCollection", features };
  }, [occFootprints]);
  useMapSymbolLayer(mapRef, mapReady, {
    id: OCC_BADGES_ID,
    data: occBadgesFC,
    layout: {
      "text-field": ["get", "badge"],
      "text-size": 10,
      "text-font": LABEL_TEXT_FONT,
      "text-line-height": 1.1,
    },
    paint: { "text-color": "#1f1f1f", "text-halo-color": "#fff", "text-halo-width": 1.5 },
  });
  useMapSymbolLayer(mapRef, mapReady, {
    id: "section-labels",
    data: sectionLabelsFC,
    minzoom: SECTION_LABEL_ZOOM,
    layout: { "text-field": ["get", "label"], "text-size": 12, "text-font": LABEL_TEXT_FONT },
    paint: LABEL_HALO,
  });

  // Legend: only the buckets actually drawn.
  const legendBuckets = useMemo(() => {
    const present = new Set(drawnRows.map((r) => statusBucket(r.deal_status)));
    return TRACT_BUCKETS.filter((b) => present.has(b.key)).map((b) => ({
      ...b,
      color: TRACT_BUCKET_COLORS[b.key],
    }));
  }, [drawnRows]);

  // Mapbox's queryRenderedFeatures errors on missing layer ids, so the
  // hover list is exactly what is mounted.
  const tractHitIds = useMemo(
    () => [
      ...(showOwn ? [setIds(OWN).fill, setIds(OWN).unresolvedFill] : []),
      ...(showNbr ? [setIds(NBR).fill, setIds(NBR).unresolvedFill] : []),
    ],
    [showOwn, showNbr]
  );
  const hoverLayerIds = useMemo(
    () => [
      ...(dotsVisible ? [SECTION_DOTS_ID] : []),
      ...(detailZoom && sectionsHitFC.features.length > 0 ? ["sections-hit"] : []),
      ...(showOcc ? [OCC_FILL_ID] : []),
      ...tractHitIds,
      ...(showIntentPoints ? [OCC_INTENTS_ID] : []),
    ],
    [dotsVisible, detailZoom, sectionsHitFC, showOcc, tractHitIds, showIntentPoints]
  );

  const hoverPopup = useMemo(
    () => ({
      layerIds: hoverLayerIds,
      render: (props) => {
        if (props && props.spot_id) {
          const permits = occIntents.intentsById.get(props.spot_id) || [];
          return permits
            .map((p) =>
              renderPopupHtml({ swatch: OCC_INTENT_COLOR, rows: occIntentRows(p, occIntentMetadata) })
            )
            .join('<hr style="margin:4px 0;border:0;border-top:1px solid #e8e8e8">');
        }
        if (props && props.footprint_id) {
          const filings = occFootprints.filingsById.get(props.footprint_id) || [];
          return filings
            .map((f) =>
              renderPopupHtml({ swatch: OCC_KIND_COLORS[f.kind], rows: occFilingRows(f, occMetadata) })
            )
            .join('<hr style="margin:4px 0;border:0;border-top:1px solid #e8e8e8">');
        }
        if (props && props.group_id) {
          const groupRows = allShapes.groups.get(props.group_id) || [];
          const dealRows = distinctDealRows(groupRows);
          const notes = [];
          if (props.unresolved) notes.push("Location unresolved");
          if (props.approximate) notes.push("Approximate");
          // One deal: its detail. Several: a summary; the side panel lists all.
          if (dealRows.length <= 1) {
            return renderPopupHtml({
              rows: dealRows.length ? tractSummaryRows(dealRows[0], tractSummaryMeta).rows : [],
              note: notes.join(" • ") || undefined,
            });
          }
          const first = groupRows[0] || {};
          notes.push("Click for the deal list");
          return renderPopupHtml({
            title: [first.trs_canonical, first.quartering].filter(Boolean).join(" · "),
            rows: groupSummaryRows(groupRows),
            note: notes.join(" • "),
          });
        }
        // Section dot: TRS, active / total deals, de-duplicated net acres,
        // and how many deals sit in each status bucket.
        if (props && props.radius !== undefined && props.trs) {
          const breakdown = TRACT_BUCKETS.filter((b) => numOrZero(props[`n_${b.key}`]) > 0)
            .map((b) => `${b.label} ${numOrZero(props[`n_${b.key}`])}`)
            .join(" · ");
          const dotAcres = numOrZero(props.net_acres);
          return (
            `<div style="font-size:12px;line-height:16px;">` +
            `<div style="font-weight:600">${esc(props.trs)}</div>` +
            `<div>${numOrZero(props.active_count)} active / ${numOrZero(props.deal_count)} total deals</div>` +
            `<div>${esc(dotAcres.toLocaleString(undefined, { maximumFractionDigits: 2 }))} net ac</div>` +
            (breakdown ? `<div>${esc(breakdown)}</div>` : "") +
            `<div style="color:#8c8c8c">Click to zoom in</div>` +
            `</div>`
          );
        }
        const trs = esc(props.trs_key ?? `${props.twp}-${props.rng}-${props.sec}`);
        const na = numOrZero(props.netAcres);
        return (
          `<div style="font-size:12px;line-height:16px;">` +
          `<div style="font-weight:600">${trs}</div>` +
          `<div>${numOrZero(props.count)} active / ${numOrZero(props.dealCount)} total deals • ` +
          `${na.toLocaleString(undefined, { maximumFractionDigits: 2 })} net ac</div>` +
          `<div>Top broker: ${esc(props.topBroker ?? "—")}</div>` +
          `</div>`
        );
      },
    }),
    [
      hoverLayerIds,
      occMetadata,
      occFootprints,
      occIntentMetadata,
      occIntents,
      tractSummaryMeta,
      allShapes,
    ]
  );

  const loading = dealsLoading || spatialLoading || occLoading || occIntentsLoading;

  const controls = (
    <Space align="center" size={16} wrap style={{ marginBottom: 8 }}>
      {setMode ? null : (
        <Checkbox checked={showNeighbours} onChange={(e) => setShowNeighbours(e.target.checked)}>
          Show neighbouring deals
        </Checkbox>
      )}
      <Checkbox
        checked={showDead}
        disabled={!setMode && !showNeighbours}
        onChange={(e) => setShowDead(e.target.checked)}
      >
        Show dead deals
      </Checkbox>
      <Space size="small">
        <Typography.Text strong>{OCC_GROUP_LABEL}</Typography.Text>
        <Checkbox.Group
          value={showIntents ? [...occKinds, OCC_INTENTS_OPTION] : occKinds}
          onChange={(v) => {
            setOccKinds(v.filter((k) => k !== OCC_INTENTS_OPTION));
            setShowIntents(v.includes(OCC_INTENTS_OPTION));
          }}
          options={[
            ...OCC_KINDS.map((k) => ({ value: k, label: occKindLabel(k) })),
            { value: OCC_INTENTS_OPTION, label: OCC_INTENTS_LABEL },
          ]}
        />
      </Space>
      {loading ? <Typography.Text type="secondary">Loading…</Typography.Text> : null}
    </Space>
  );

  // --- Mapbox token states ---
  if (mapboxTokenLoading) {
    return (
      <div style={{ height, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spin size="large" tip="Loading Mapbox token…" />
      </div>
    );
  }
  if (!mapboxToken) {
    return (
      <Alert
        type="error"
        showIcon
        message="Mapbox token unavailable"
        description={
          mapboxTokenErr
            ? `Failed to load Mapbox token from Key Vault: ${mapboxTokenErr}`
            : "No Mapbox token available from Key Vault or VITE_MAPBOX_TOKEN."
        }
      />
    );
  }

  // --- Empty state: no tracts, or none that can be placed on the grid ---
  const sectionsSettled = !spatialLoading && sections !== null;
  const noMappedTracts =
    !dealsLoading &&
    !dealsErr &&
    (own.length === 0 || ownCounties.length === 0 || (sectionsSettled && !hasOwnShapes));
  // Set mode keeps its controls: "Show dead deals" may be what brings tracts back.
  if (noMappedTracts && !setMode) {
    return <Empty description="No mapped tracts for this deal" />;
  }

  return (
    <div>
      {controls}
      {dealsErr || spatialErr || occErr || occIntentsErr ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 8 }}
          message={[
            dealsErr ? `Failed to load deals: ${dealsErr}` : null,
            spatialErr ? `Failed to load spatial layers: ${spatialErr}` : null,
            occErr ? `Failed to load OCC units: ${occErr}` : null,
            occIntentsErr ? `Failed to load OCC intents to drill: ${occIntentsErr}` : null,
          ]
            .filter(Boolean)
            .join(" • ")}
        />
      ) : null}
      <div style={{ position: "relative", height, width: "100%" }}>
        {/* Over the map rather than instead of it, so the map instance (and
            its layers) survive a filter that briefly matches nothing. */}
        {noMappedTracts ? (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: legendTheme === "dark" ? "rgba(20,20,20,0.75)" : "rgba(255,255,255,0.75)",
            }}
          >
            <Empty description="No mapped tracts match the current filter" />
          </div>
        ) : null}
        <WigMap
          accessToken={mapboxToken}
          mapStyle={MAP_STYLE}
          initialViewState={FALLBACK_VIEW}
          layers={layers}
          hoverPopup={hoverPopup}
          onClick={(ev) => {
            // Section dot: fly into that section's tract shapes. The dot layer
            // is a map-instance layer WigMap doesn't query, so ask the map.
            const map = mapRef.current;
            const point = ev?.originalEvent?.point;
            if (dotsVisible && map && point && map.getLayer?.(SECTION_DOTS_ID)) {
              const [dot] = map.queryRenderedFeatures(point, { layers: [SECTION_DOTS_ID] });
              if (dot) {
                map.flyTo({ center: dot.geometry.coordinates, zoom: DOT_CLICK_ZOOM });
                return;
              }
            }
            const tractFeat = (ev?.features || []).find((f) => tractHitIds.includes(f?.layer?.id));
            if (tractFeat) {
              const groupId = tractFeat.properties?.group_id;
              const groupDeals = distinctDealRows(allShapes.groups.get(groupId));
              if (groupDeals.length) setSelectedTract({ groupId, deals: groupDeals });
            }
          }}
          height="100%"
          width="100%"
          showScaleControl
          onMapReady={(m) => {
            mapRef.current = m;
            setMapReady(true);
          }}
        />
        <Drawer
          open={!!selectedTract}
          onClose={() => setSelectedTract(null)}
          mask={false}
          placement="right"
          width={380}
          title={
            selectedTract
              ? [
                  [
                    selectedTract.deals[0].section,
                    selectedTract.deals[0].township,
                    selectedTract.deals[0].range,
                  ]
                    .filter(Boolean)
                    .join("-"),
                  selectedTract.deals[0].quartering,
                ]
                  .filter(Boolean)
                  .join(" ")
              : null
          }
        >
          {selectedTract && selectedTract.deals.length > 1 ? (
            <>
              <Descriptions
                size="small"
                column={1}
                items={groupSummaryRows(allShapes.groups.get(selectedTract.groupId)).map((row) => ({
                  key: row.label,
                  label: row.label,
                  children: row.text,
                }))}
              />
              <Divider style={{ margin: "12px 0" }} />
            </>
          ) : null}
          {(selectedTract?.deals || []).map((r) => (
            <div key={r.deal_id} style={{ marginBottom: 16 }}>
              <Descriptions
                size="small"
                column={1}
                items={tractSummaryRows(r, tractSummaryMeta).rows.map((row) => ({
                  key: row.field,
                  label: row.label,
                  children: row.text,
                }))}
              />
              {String(r.deal_id) === String(dealId) ? (
                <Typography.Text strong style={{ display: "block", marginTop: 8 }}>
                  This deal
                </Typography.Text>
              ) : r.deal_key ? (
                <Button
                  type="primary"
                  size="small"
                  style={{ marginTop: 8 }}
                  onClick={() => navigate(`/deals/${encodeURIComponent(r.deal_key)}`)}
                >
                  Open deal
                </Button>
              ) : null}
            </div>
          ))}
        </Drawer>
        <DealsMapLegend
          mode="tracts"
          theme={legendTheme}
          activeDefinition={ACTIVE_DEFINITION}
          tractBuckets={legendBuckets}
          occTitle={OCC_GROUP_LABEL}
          occKinds={[
            ...drawnOccKinds.map((k) => ({
              kind: k,
              label: `${OCC_KIND_LETTERS[k]} · ${occKindLabel(k)}`,
              color: OCC_KIND_COLORS[k],
            })),
            ...(showIntentPoints
              ? [
                  {
                    kind: OCC_INTENTS_OPTION,
                    label: OCC_INTENTS_LABEL,
                    color: OCC_INTENT_COLOR,
                    shape: "dot",
                  },
                ]
              : []),
          ]}
        />
      </div>
    </div>
  );
};

export default DealMap;
